import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { MessageCircle, Sparkles, AlertCircle } from "lucide-react";
import { FEES, waLink } from "../../lib/constants";

const CategoryCard = ({ cat }) => (
  <div
    className={`bg-[#1A1A1A] border ${
      cat.highlight ? "border-[#FACC15]/60" : "border-white/5"
    } rounded-sm p-6 md:p-7 relative overflow-hidden`}
  >
    {cat.highlight && (
      <div className="absolute top-0 right-0 bg-[#FACC15] text-[#0F0F0F] text-[10px] uppercase tracking-[0.2em] font-bold px-3 py-1 flex items-center gap-1">
        <Sparkles className="w-3 h-3" /> Premium
      </div>
    )}
    <div className="mb-5">
      <h4 className="font-display text-xl font-semibold text-white">
        {cat.title}
      </h4>
      <div className="text-xs text-[#FACC15] tracking-wide uppercase mt-1 font-semibold">
        {cat.timing}
      </div>
    </div>
    <div className="space-y-2">
      {cat.plans.map((p, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-3 py-3 border-b border-white/5 last:border-0"
        >
          <div>
            <div className="text-sm text-white font-medium">{p.name}</div>
            <div className="text-xs text-white/50 mt-0.5">{p.remark}</div>
          </div>
          <div className="font-display text-xl font-bold text-[#FACC15] whitespace-nowrap">
            {p.price}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const FeesPanel = ({ data, branchKey }) => (
  <div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
      {data.categories.map((cat, i) => (
        <CategoryCard key={i} cat={cat} />
      ))}
    </div>

    <div className="mt-6 flex flex-col sm:flex-row items-stretch sm:items-center gap-4 bg-[#211E1A] border border-white/5 rounded-sm p-5">
      <div className="flex-1">
        <div className="text-sm text-white/85">
          <span className="text-[#FACC15] font-semibold">Add-on:</span>{" "}
          {data.addon}
        </div>
        <div className="text-xs text-white/45 mt-1">
          Pricing subject to change. Contact us for current seat availability.
        </div>
      </div>
      <a
        href={waLink(
          `Hi, I want to know seat availability for ${data.branch}.`
        )}
        target="_blank"
        rel="noreferrer"
        data-testid={`fees-${branchKey}-whatsapp`}
        className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5C] text-white font-semibold px-5 py-3 text-sm rounded-sm whitespace-nowrap transition-all hover:-translate-y-0.5"
      >
        <MessageCircle className="w-4 h-4" /> Contact for Seat Availability
      </a>
    </div>
  </div>
);

export default function Fees() {
  return (
    <section
      id="fees"
      data-testid="fees-section"
      className="relative py-24 md:py-32 bg-[#0F0F0F]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-12 max-w-2xl">
          <div className="text-xs tracking-[0.25em] uppercase text-[#FACC15] font-semibold mb-4">
            Fees Structure
          </div>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold text-white">
            Transparent pricing.
            <br />
            <span className="text-white/40">No hidden fees.</span>
          </h2>
        </div>

        <Tabs defaultValue="s22" className="w-full">
          <TabsList
            data-testid="fees-tabs"
            className="bg-transparent border-b border-white/10 rounded-none w-full justify-start gap-0 p-0 h-auto mb-10"
          >
            <TabsTrigger
              value="s22"
              data-testid="fees-tab-s22"
              className="bg-transparent rounded-none px-6 py-4 text-sm md:text-base font-medium text-white/50 data-[state=active]:bg-transparent data-[state=active]:text-[#FACC15] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#FACC15] -mb-px"
            >
              Sector 22 Noida
              <span className="ml-2 text-[10px] uppercase tracking-wider text-white/40">
                24/7
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="s26"
              data-testid="fees-tab-s26"
              className="bg-transparent rounded-none px-6 py-4 text-sm md:text-base font-medium text-white/50 data-[state=active]:bg-transparent data-[state=active]:text-[#FACC15] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-[#FACC15] -mb-px"
            >
              Sector 26 Noida
              <span className="ml-2 text-[10px] uppercase tracking-wider text-white/40">
                8AM–10PM
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="s22" data-testid="fees-panel-s22">
            <FeesPanel data={FEES.s22} branchKey="s22" />
          </TabsContent>
          <TabsContent value="s26" data-testid="fees-panel-s26">
            <FeesPanel data={FEES.s26} branchKey="s26" />
          </TabsContent>
        </Tabs>

        <div className="mt-14 bg-[#1A1A1A] border border-white/5 rounded-sm p-6 md:p-8">
          <div className="flex items-start gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-[#FACC15] mt-0.5 flex-shrink-0" />
            <h4 className="font-display text-lg font-semibold text-white">
              Important Notes
            </h4>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-white/70 list-disc list-inside ml-2">
            <li>Membership is non-extendable, non-refundable & non-transferable.</li>
            <li>Vacant seats for more than 1 hour may be reassigned.</li>
            <li>Sector 22 branch is open 24/7.</li>
            <li>Sector 26 branch timings are 8 AM to 10 PM.</li>
            <li className="md:col-span-2">
              Library may remain closed for one day every month. Closure date
              will be announced in advance by the management.
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
}
